import { useEffect, useState } from "react";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/components/ModalContainer";
import Input from "@/components/Input";
import { useTranslation } from "react-i18next";

const GoogleIcon = () => (
  <img
    className="w-5 h-5"
    src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCI+PHBhdGggZmlsbD0iIzQyODVGNCIgZD0iTTIyLjU2IDEyLjI1YzAtLjc4LS4wNy0xLjUzLS4yLTIuMjVIMTJ2NC4yNmg1LjkyYy0uMjYgMS4zNy0xLjA0IDIuNTMtMi4yMSAzLjMxdjIuNzdoMy41N2MyLjA4LTEuOTIgMy4yOC00Ljc0IDMuMjgtOC4wOXoiLz48cGF0aCBmaWxsPSIjMzRBODUzIiBkPSJNMTIgMjNjMi45NyAwIDUuNDYtLjk4IDcuMjgtMi42NmwtMy41Ny0yLjc3Yy0uOTguNjYtMi4yMyAxLjA2LTMuNzEgMS4wNi0yLjg2IDAtNS4yOS0xLjkzLTYuMTYtNC41M0gyLjE4djIuODRDMy45OSAyMC41MyA3LjcgMjMgMTIgMjN6Ii8+PHBhdGggZmlsbD0iI0ZCQkMwNSIgZD0iTTUuODQgMTQuMDljLS4yMi0uNjYtLjM1LTEuMzYtLjM1LTIuMDlzLjEzLTEuNDMuMzUtMi4wOVY3LjA3SDIuMThDMS40MyA4LjU1IDEgMTAuMjIgMSAxMnMuNDMgMy40NSAxLjE4IDQuOTNsMi44NS0yLjIyLjgxLS42MnoiLz48cGF0aCBmaWxsPSIjRUE0MzM1IiBkPSJNMTIgNS4zOGMxLjYyIDAgMy4wNi41NiA0LjIxIDEuNjRsMy4xNS0zLjE1QzE3LjQ1IDIuMDkgMTQuOTcgMSAxMiAxIDcuNyAxIDMuOTkgMy40NyAyLjE4IDcuMDdsMy42NiAyLjg0Yy44Ny0yLjYgMy4zLTQuNTMgNi4xNi00LjUzeiIvPjxwYXRoIGZpbGw9Im5vbmUiIGQ9Ik0xIDFoMjJ2MjJIMXoiLz48L3N2Zz4="
    alt="Google"
  />
);

const AlertPromptSignIn = () => {
  const prompt = useUISlice((state) => state.alertPromptSignIn);
  const setPrompt = useUISlice((state) => state.setAlertPromptSignIn);
  const [text, setText] = useState("");
  const width = useUISlice((state) => state.device.width);
  const { t } = useTranslation();

  useEffect(() => {
    if (!prompt) return;

    setText(prompt.defaultValue || "");
  }, [prompt]);

  const handleSubmit = () => {
    prompt?.button?.onPress(text);
  };

  const handleDismiss = () => {
    prompt?.onDismiss?.();
    setPrompt(undefined);
  };

  if (!prompt) return null;

  return (
    <ModalContainer
      title={prompt.title}
      subtitle={prompt.subtitle}
      onDismiss={handleDismiss}
      primaryButtonText={prompt.button?.text || t("useAuth.signInButton")}
      onPrimaryButtonPress={handleSubmit}
      hideDismissIcon={prompt.hideDismissIcon}
      hideCancel
      shouldPersistBackdropClick={prompt.shouldPersistBackdropClick}
      loading={prompt.loading}
      destructive={prompt.button && prompt.button.destructive}
      modalWidth={prompt.multiLine ? Math.min(width - 32, 500) : 360}
    >
      <div className="h-4" />

      {prompt.onGoogleSignIn && (
        <>
          <button
            data-action-id="auth.google.submit"
            onClick={prompt.onGoogleSignIn}
            disabled={prompt.loading}
            className="flex flex-row items-center justify-center w-full h-12 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:cursor-wait disabled:opacity-60"
          >
            <GoogleIcon />
            <span className="ml-3 text-base font-medium text-zinc-900">
              {prompt.googleButtonText || t("useAuth.signInWithGoogle")}
            </span>
          </button>

          <p className="text-center my-5 text-gray-500 text-sm">
            {t("useAuth.or").toUpperCase()}
          </p>
        </>
      )}

      <Input
        disabled={prompt.loading}
        height={prompt.multiLine ? "220px" : undefined}
        multiLine={prompt.multiLine}
        autoFocus={true}
        value={text}
        placeholder={prompt.placeholder}
        inputMode={prompt.inputMode}
        autoComplete={prompt.autoComplete}
        pattern={prompt.pattern}
        onTextChange={(text) => setText(text)}
      />
    </ModalContainer>
  );
};

export default AlertPromptSignIn;
